import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { fetchDashboardData, getWebSocketUrl, type DashboardData, type JsonRecord } from './api.js';
import { calculatePositionTakeHome, LiveMarketModal, type LiveModalSelection } from './LiveMarketModal.js';
import { StrategyDetail } from './StrategyDetail.js';
import { StrategyManagement } from './StrategyManagement.js';
import { parseAppRoute } from './routing.js';

export const dashboardFallbackRefreshIntervalMs = 5 * 60_000;

const dashboardRefreshUpdateTypes = new Set([
  'discovery',
  'signal',
  'execution-attempt',
  'settlement',
  'position-closed',
  'websocket',
  'geoblock',
  'leadership',
]);

/**
 * Tests whether a monitoring WebSocket update requires a fresh durable database snapshot.
 * @param update - Parsed monitoring update.
 * @returns True only for state changes that cannot be applied from the update itself.
 */
export function shouldRefreshDashboardForUpdate(update: JsonRecord): boolean {
  return dashboardRefreshUpdateTypes.has(String(update.type ?? ''));
}

/**
 * Formats an unknown numeric value as US dollars.
 * @param value - Numeric-like value.
 * @returns Formatted dollar string.
 */
function dollars(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—';
}

/**
 * Formats an unknown date value for concise monitoring display.
 * @param value - Date-like value.
 * @returns Localized date string.
 */
function dateTime(value: unknown): string {
  if (value === null || value === undefined) return 'Never';
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

/**
 * Formats a probability on a scale of one as cents.
 * @param value - Numeric-like probability.
 * @returns Cents label.
 */
export function formatCents(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${(numericValue * 100).toFixed(1)}¢` : '—';
}

/**
 * Converts a machine-readable status or rejection code into operator-facing text.
 * @param value - Status-like value.
 * @returns Human-readable label.
 */
function humanizeCode(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Pending';
  const normalized = String(value).toLowerCase().replaceAll('_', ' ');
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

/**
 * Formats a recent age in seconds for health monitoring.
 * @param value - Numeric-like age in seconds.
 * @returns Concise freshness label.
 */
function freshness(value: unknown): string {
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return 'No data';
  if (seconds < 2) return 'Live now';
  if (seconds < 60) return `${Math.floor(seconds)}s ago`;
  return `${Math.floor(seconds / 60)}m ago`;
}

/**
 * Formats a scale-100 percentage without inventing a value for missing data.
 * @param value - Numeric-like percentage on a 0–100 scale.
 * @returns Percentage label or an em dash.
 */
function percent100(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${numericValue.toFixed(1)}%` : '—';
}

/**
 * Reads one property from a potentially absent JSON object.
 * @param record - JSON record or unknown value.
 * @param key - Property name.
 * @returns Property value.
 */
function field(record: unknown, key: string): unknown {
  return typeof record === 'object' && record !== null ? (record as JsonRecord)[key] : undefined;
}

/**
 * Applies a lightweight live price update without refetching every dashboard endpoint.
 * @param dashboard - Current dashboard payload.
 * @param update - Monitoring WebSocket update.
 * @returns Updated dashboard payload.
 */
export function applyMarketDataUpdate(dashboard: DashboardData, update: JsonRecord): DashboardData {
  if (update.type !== 'market-data' || typeof update.data !== 'object' || update.data === null) return dashboard;
  const data = update.data as JsonRecord;
  const tokenId = String(data.tokenId ?? '');
  const price = typeof data.price === 'object' && data.price !== null ? data.price as JsonRecord : null;
  if (tokenId.length === 0 || price === null) return dashboard;
  const markets = dashboard.markets.map((market) => {
    const outcomes = Array.isArray(market.outcomes) ? market.outcomes as JsonRecord[] : [];
    if (!outcomes.some((outcome) => outcome.tokenId === tokenId)) return market;
    const updatedOutcomes = outcomes.map((outcome) => {
      if (outcome.tokenId !== tokenId) return outcome;
      const bestBidPercent1 = field(price, 'bestBidPercent1');
      const bestAskPercent1 = field(price, 'bestAskPercent1');
      const bid = bestBidPercent1 === null ? null : Number(bestBidPercent1);
      const ask = bestAskPercent1 === null ? null : Number(bestAskPercent1);
      return {
        ...outcome,
        ...price,
        midpointPercent1: bid !== null && ask !== null && Number.isFinite(bid) && Number.isFinite(ask) ? (bid + ask) / 2 : null,
      };
    });
    const favorite = updatedOutcomes
      .filter((outcome) => Number.isFinite(Number(outcome.midpointPercent1)))
      .sort((left, right) => Number(right.midpointPercent1) - Number(left.midpointPercent1))[0];
    return {
      ...market,
      outcomes: updatedOutcomes,
      favoriteLabel: favorite?.label ?? null,
      favoriteBestBidPercent1: favorite?.bestBidPercent1 ?? null,
      favoriteBestAskPercent1: favorite?.bestAskPercent1 ?? null,
      favoriteMidpointPercent1: favorite?.midpointPercent1 ?? null,
      referencePricePercent1: favorite?.lastTradePricePercent1 ?? null,
      referencePriceAt: favorite?.lastTradeAt ?? null,
    };
  });
  const currentPriceValue = price.bestBidPercent1 ?? price.lastTradePricePercent1;
  const currentPricePercent1 = currentPriceValue === null || currentPriceValue === undefined
    ? null
    : Number(currentPriceValue);
  const positions = dashboard.positions.map((position) => {
    if (position.token_id !== tokenId || position.status !== 'OPEN' || currentPricePercent1 === null || !Number.isFinite(currentPricePercent1)) return position;
    const takeHome = calculatePositionTakeHome(position, currentPricePercent1);
    const liquidationValueDollars = takeHome.grossTakeHomeDollars - takeHome.estimatedExitFeeDollars;
    return {
      ...position,
      currentPricePercent1,
      currentGrossTakeHomeDollars: takeHome.grossTakeHomeDollars,
      currentNetTakeHomeDollars: takeHome.netTakeHomeDollars,
      currentLiquidationValueDollars: liquidationValueDollars,
      currentNetPnlDollars: takeHome.netPnlDollars,
      estimatedExitFeeDollars: takeHome.estimatedExitFeeDollars,
    };
  });
  const account = field(dashboard.config, 'account');
  const cashDollars = Number(field(account, 'cashDollars'));
  const markedPositionValueDollars = positions.filter((position) => position.status === 'OPEN').reduce((sum, position) => (
    sum + Number(position.currentLiquidationValueDollars ?? position.gross_notional_dollars ?? 0)
  ), 0);
  return {
    ...dashboard,
    markets,
    positions,
    config: {
      ...dashboard.config,
      account: typeof account === 'object' && account !== null ? {
        ...(account as JsonRecord),
        markedPositionValueDollars,
        equityDollars: Number.isFinite(cashDollars) ? cashDollars + markedPositionValueDollars : null,
      } : account,
    },
  };
}

/**
 * Applies one coalesced browser-facing batch of latest token updates.
 * @param dashboard - Current dashboard payload.
 * @param update - Monitoring WebSocket batch update.
 * @returns Dashboard payload after every valid token update is applied.
 */
export function applyMarketDataBatchUpdate(dashboard: DashboardData, update: JsonRecord): DashboardData {
  if (update.type !== 'market-data-batch' || !Array.isArray(update.data)) return dashboard;
  return update.data.reduce<DashboardData>((current, data) => (
    typeof data === 'object' && data !== null
      ? applyMarketDataUpdate(current, { type: 'market-data', data })
      : current
  ), dashboard);
}

/**
 * Renders a small labeled metric tile.
 * @param props - Label, value, and optional tone.
 * @returns Metric tile.
 */
function Metric(props: { readonly label: string; readonly value: ReactNode; readonly tone?: 'good' | 'warn' | 'neutral' }): ReactNode {
  return (
    <div className={`metric metric--${props.tone ?? 'neutral'}`}>
      <span>{props.label}</span>
      <strong>{props.value}</strong>
    </div>
  );
}

/**
 * Renders a panel with consistent title and optional right-side detail.
 * @param props - Panel content.
 * @returns Panel element.
 */
function Panel(props: { readonly title: string; readonly detail?: ReactNode; readonly className?: string; readonly children: ReactNode }): ReactNode {
  return (
    <section className={`panel ${props.className ?? ''}`}>
      <header className="panel__header"><h2>{props.title}</h2>{props.detail}</header>
      {props.children}
    </section>
  );
}

/**
 * Renders an empty-state row.
 * @param props - Human-readable explanation.
 * @returns Empty-state element.
 */
function Empty(props: { readonly children: ReactNode }): ReactNode {
  return <div className="empty">{props.children}</div>;
}

/** Main PredictionTrader monitoring dashboard. */
export function App(): ReactNode {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<'CONNECTING' | 'LIVE' | 'RECONNECTING'>('CONNECTING');
  const [modalSelection, setModalSelection] = useState<LiveModalSelection | null>(null);
  const refreshPending = useRef(false);
  const route = parseAppRoute(window.location.pathname);

  const refresh = useCallback(async (): Promise<void> => {
    if (refreshPending.current) return;
    refreshPending.current = true;
    try {
      setData(await fetchDashboardData());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      refreshPending.current = false;
    }
  }, []);

  useEffect(() => {
    void refresh();
    const poll = window.setInterval(() => void refresh(), dashboardFallbackRefreshIntervalMs);
    let socket: WebSocket;
    let reconnect: number | undefined;
    let liveRefresh: number | undefined;
    const connect = (): void => {
      setConnection((current) => current === 'CONNECTING' ? 'CONNECTING' : 'RECONNECTING');
      socket = new WebSocket(getWebSocketUrl());
      socket.addEventListener('open', () => setConnection('LIVE'));
      socket.addEventListener('message', (event) => {
        try {
          const parsed: unknown = JSON.parse(String(event.data));
          if (typeof parsed === 'object' && parsed !== null && (parsed as JsonRecord).type === 'market-data') {
            setData((current) => current === null ? current : applyMarketDataUpdate(current, parsed as JsonRecord));
            return;
          }
          if (typeof parsed === 'object' && parsed !== null && (parsed as JsonRecord).type === 'market-data-batch') {
            setData((current) => current === null ? current : applyMarketDataBatchUpdate(current, parsed as JsonRecord));
            return;
          }
          if (typeof parsed !== 'object' || parsed === null || !shouldRefreshDashboardForUpdate(parsed as JsonRecord)) return;
        } catch {
          return;
        }
        if (liveRefresh !== undefined) return;
        liveRefresh = window.setTimeout(() => {
          liveRefresh = undefined;
          void refresh();
        }, 1_000);
      });
      socket.addEventListener('close', () => {
        setConnection('RECONNECTING');
        reconnect = window.setTimeout(connect, 2_000);
      });
    };
    connect();
    return () => {
      window.clearInterval(poll);
      if (reconnect !== undefined) window.clearTimeout(reconnect);
      if (liveRefresh !== undefined) window.clearTimeout(liveRefresh);
      socket.close();
    };
  }, [refresh]);

  const account = field(data?.config, 'account');
  const activeStrategy = field(data?.config, 'strategy');
  const prospective = field(data?.statistics, 'prospective');
  const websocketStatus = String(field(data?.status, 'websocketStatus') ?? 'IDLE');
  const marketFeedLabel = data === null ? 'loading' : websocketStatus === 'IDLE' ? 'waiting for markets' : websocketStatus.toLowerCase();
  const marketFeedTone = websocketStatus === 'CONNECTED' ? 'good' : data === null || websocketStatus === 'IDLE' ? 'neutral' : 'warn';
  const uptimeSeconds = Number(field(data?.status, 'uptimeSeconds') ?? 0);
  const openPositions = useMemo(() => data?.positions.filter((position) => position.status === 'OPEN') ?? [], [data]);
  const equityDollars = field(account, 'equityDollars');
  const serviceReady = data === null ? null : field(data.readiness, 'ready') === true;
  const visibleActivity = useMemo(() => {
    if (data === null) return [];
    let discoveryIncluded = false;
    let serviceStartIncluded = false;
    return data.activity.filter((item) => {
      if (item.eventType === 'DISCOVERY_FAILED' && field(data.status, 'lastDiscoveryError') === null) {
        return false;
      }
      if (item.eventType === 'DISCOVERY_COMPLETE') {
        if (discoveryIncluded) return false;
        discoveryIncluded = true;
      }
      if (item.eventType === 'SERVICE_STARTED') {
        if (serviceStartIncluded) return false;
        serviceStartIncluded = true;
      }
      return true;
    }).slice(0, 12);
  }, [data]);

  return (
    <main>
      <nav className="topbar">
        <a className="brand" href="/" aria-label="PredictionTrader home">
          <span className="brand__mark">PT</span>
          <span><b>PredictionTrader</b><small>Prediction-market operations</small></span>
        </a>
        <div className="topbar__status">
          <span className={`pulse ${connection === 'LIVE' ? 'pulse--live' : ''}`} />
          {connection === 'LIVE' ? 'Live monitor' : 'Reconnecting'}
          <span className="mode">Paper</span>
        </div>
      </nav>

      <div className="shell">
        {route.kind === 'OVERVIEW' ? <StrategyManagement />
          : route.kind === 'STRATEGY' && route.strategyKey !== 'lol-pregame-favorite' ? <StrategyDetail strategyKey={route.strategyKey} />
          : route.kind === 'NOT_FOUND' ? <div className="strategy-page"><a className="back-link" href="/">← Overview</a><section className="panel not-found"><h1>Page not found</h1><p>Use the Overview to open a strategy page.</p></section></div>
          : <>
        <a className="back-link" href="/">← Overview</a>
        <header className="hero">
          <div>
            <p className="eyebrow">Strategy control room</p>
            <h1>LoL pregame favorite <span>75–80¢</span></h1>
            <p>In paper mode, buys full-match LoL favorites after a 75–&lt;80¢ public trade, 5 minutes–72 hours before start. The 2-second book must fill 5% of equity within 2¢; at most two positions stay open until official resolution or a fully executable exit after a 99.9¢/0.1¢ split.</p>
          </div>
          <div className="hero__meta">
            <span>Route · version</span>
            <strong>/strategy/lol-pregame-favorite · {String(field(activeStrategy, 'version') ?? 'loading')}</strong>
          </div>
        </header>

        {error !== null && <div className="alert"><b>Backend unavailable</b><span>{error}</span></div>}

        <section className="metric-grid" aria-label="Account and service summary">
          <Metric label="Paper equity" value={dollars(equityDollars)} tone="good" />
          <Metric label="Available cash" value={dollars(field(account, 'cashDollars'))} />
          <Metric label="Position value" value={dollars(field(account, 'markedPositionValueDollars'))} />
          <Metric label="Open positions" value={data === null ? '—' : `${openPositions.length} / 2`} />
          <Metric label="Market feed" value={marketFeedLabel} tone={marketFeedTone} />
          <Metric label="Uptime" value={data === null ? '—' : `${Math.floor(uptimeSeconds / 3_600)}h ${Math.floor((uptimeSeconds % 3_600) / 60)}m`} />
        </section>

        <div className="dashboard-grid">
          <Panel title="Tracked markets" detail={<span className="count">{data === null ? '—' : data.markets.length} monitored</span>} className="span-2 market-panel">
            {data === null ? <Empty>Loading eligible market inventory…</Empty> : data.markets.length === 0 ? <Empty>No eligible LoL full-match moneylines in the next 72 hours.</Empty> : (
              <div className="table-wrap market-table"><table><thead><tr><th>Match</th><th>Start</th><th>Favorite market</th><th>Status</th></tr></thead><tbody>
                {data.markets.map((market) => <tr className="clickable-row" role="button" tabIndex={0} aria-label={`Open live market for ${String(market.title)}`} onClick={() => setModalSelection({ kind: 'market', marketId: String(market.id) })} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setModalSelection({ kind: 'market', marketId: String(market.id) }); }} key={String(market.id)}><td><b>{String(market.title)}</b><small>{String(market.tournament ?? 'League of Legends')}</small></td><td>{dateTime(market.scheduledStartAt)}</td><td><b>{String(market.favoriteLabel ?? 'Awaiting quote')}</b><small>{formatCents(market.favoriteBestAskPercent1)} ask · {formatCents(market.referencePricePercent1)} latest trade</small></td><td><span className="tag tag--good">Monitoring</span></td></tr>)}
              </tbody></table></div>
            )}
          </Panel>

          <Panel title="System health" detail={<span className={`tag ${serviceReady === null ? 'tag--neutral' : serviceReady ? 'tag--good' : 'tag--warn'}`}>{serviceReady === null ? 'Checking' : serviceReady ? 'Healthy' : 'Unready'}</span>} className="health-panel">
            {data === null ? <Empty>Checking backend, database, and market feed…</Empty> : <div className="health-list">
              <div><span>Overall service</span><b>{serviceReady ? 'Ready' : 'Needs attention'}</b></div>
              <div><span>Database</span><b>{field(data?.readiness, 'databaseReady') === true ? 'Connected' : 'Unavailable'}</b></div>
              <div><span>Market data</span><b>{freshness(field(data?.readiness, 'marketDataAgeSeconds'))}</b></div>
              <div><span>Market feed</span><b>{humanizeCode(websocketStatus)}</b></div>
              <div><span>Watcher leadership</span><b>{field(data?.status, 'watcherLeader') === true ? 'Leader' : 'Standby'}</b></div>
              <div><span>Last discovery</span><b>{dateTime(field(data?.status, 'lastDiscoveryAt'))}</b></div>
              <div><span>Eligible markets</span><b>{String(field(data?.status, 'discoveredMarketCount') ?? 0)}</b></div>
              <div><span>Subscribed tokens</span><b>{String(field(data?.status, 'subscribedTokenCount') ?? 0)}</b></div>
              <div><span>Execution mode</span><b>Paper only</b></div>
            </div>}
          </Panel>

          <Panel title="Strategy activity" detail={<span className="count">primary · 2 second fill</span>} className="span-2">
            {data === null ? <Empty>Loading signals and execution attempts…</Empty> : data.signals.length === 0 ? <Empty>No eligible 75–80¢ signal has fired yet.</Empty> : (
              <div className="table-wrap"><table><thead><tr><th>Match</th><th>Signal</th><th>Primary result</th><th>Status</th></tr></thead><tbody>
                {data.signals.slice(0, 8).map((signal) => {
                  const signalId = String(signal.id);
                  const market = data.markets.find((candidate) => candidate.id === signal.marketId);
                  const primaryAttempt = data.attempts.find((attempt) => attempt.signalId === signalId && Number(attempt.latencyMs) === 2_000);
                  const accepted = primaryAttempt?.accepted === true;
                  const status = String(signal.status ?? 'SIGNAL_RECORDED');
                  return <tr key={signalId}><td><b>{String(market?.title ?? signal.eventExternalId ?? 'Unknown match')}</b><small>{dateTime(signal.signalAt)}</small></td><td>{formatCents(signal.referencePricePercent1)}</td><td><b>{primaryAttempt === undefined ? 'Pending' : accepted ? 'Would fill' : humanizeCode(primaryAttempt.rejectionReason)}</b><small>{primaryAttempt === undefined ? 'Awaiting measurement' : `${formatCents(primaryAttempt.bestAskPercent1)} best ask`}</small></td><td><span className={`tag ${status === 'OPEN' || status === 'PAPER_FILLED' ? 'tag--good' : status === 'REJECTED' ? 'tag--warn' : 'tag--neutral'}`}>{humanizeCode(status)}</span></td></tr>;
                })}
              </tbody></table></div>
            )}
          </Panel>

          <Panel title="Prospective evidence">
            <div className="evidence-number"><strong>{data === null ? '—' : String(field(prospective, 'settledTrades') ?? 0)}</strong><span>completed trades</span></div>
            <div className="health-list compact">
              <div><span>Signals recorded</span><b>{data === null ? '—' : String(field(prospective, 'attemptedSignals') ?? 0)}</b></div>
              <div><span>Accepted trades</span><b>{data === null ? '—' : String(field(prospective, 'acceptedTrades') ?? 0)}</b></div>
              <div><span>Win rate</span><b>{percent100(field(prospective, 'winRatePercent100'))}</b></div>
              <div><span>Expectancy</span><b>{dollars(field(prospective, 'expectancyDollars'))}</b></div>
              <div><span>Max drawdown</span><b>{percent100(field(prospective, 'maximumDrawdownPercent100'))}</b></div>
            </div>
            <p className="warning">Assume prospective performance is zero or negative until the frozen evaluation completes.</p>
          </Panel>

          <Panel title="Open positions" detail={<span className="count">resolution or executable extreme exit</span>} className="span-3">
            {data === null ? <Empty>Loading paper positions…</Empty> : openPositions.length === 0 ? <Empty>No capital is currently locked.</Empty> : (
              <div className="position-list">{openPositions.map((position) => {
                const market = data?.markets.find((candidate) => candidate.id === position.market_id);
                return <button type="button" className="position-card" onClick={() => setModalSelection({ kind: 'position', marketId: String(position.market_id), position })} key={String(position.id)}><div><b>{String(market?.title ?? position.event_external_id)}</b><span>{String(position.token_id).slice(0, 12)}…</span></div><strong>{dollars(position.currentLiquidationValueDollars ?? position.gross_notional_dollars)}</strong><small>{Number(position.shares).toFixed(2)} shares · {formatCents(position.currentPricePercent1)} now · entered {formatCents(position.raw_entry_price_percent1)}</small></button>;
              })}</div>
            )}
          </Panel>

          <Panel title="Recent activity" detail={<span className="count">signals · fills · feed</span>} className="span-3">
            {data === null ? <Empty>Loading recent service activity…</Empty> : visibleActivity.length === 0 ? <Empty>Activity will appear when discovery and streaming begin.</Empty> : (
              <ol className="activity-list">{visibleActivity.map((item) => <li key={String(item.id)}><span className={`event-dot event-dot--${String(item.severity).toLowerCase()}`} /><div><b>{String(item.message)}</b><small>{String(item.source)} · {dateTime(item.createdAt)}</small></div><span className="event-type">{String(item.eventType).replaceAll('_', ' ')}</span></li>)}</ol>
            )}
          </Panel>
        </div>
        </>}
      </div>
      {modalSelection !== null && <LiveMarketModal selection={modalSelection} onClose={() => setModalSelection(null)} />}
    </main>
  );
}
